/**
 * The EyeMed code setup callables (functions/src/integrations) — the
 * Integrations page's EyeMed rows and setup popup talk to these only; no
 * login state is readable from Firestore by the client.
 */
import { httpsCallable } from 'firebase/functions'
import { functions } from '../config'
import type { EyemedLoginState } from '../../shared/eyemedLogin'

export interface EyemedClinicLoginState {
  clinicId: string
  state: EyemedLoginState
}

/** Each clinic with an EyeMed record and its login's state (admins, staff in view mode too). */
export async function listEyemedLoginStates(orgId: string): Promise<EyemedClinicLoginState[]> {
  const fn = httpsCallable<{ orgId: string }, { clinics: EyemedClinicLoginState[] }>(functions, 'listEyemedLoginStates')
  const res = await fn({ orgId })
  return res.data.clinics
}

export type OpenEyemedSetupResult = { address: string } | { noDomain: true }

/** Open the setup: the address to enter in EyeMed, or `noDomain` when none could be made. */
export async function openEyemedCodeSetup(orgId: string, clinicId: string): Promise<OpenEyemedSetupResult> {
  const fn = httpsCallable<{ orgId: string; clinicId: string }, OpenEyemedSetupResult>(functions, 'openEyemedCodeSetup')
  const res = await fn({ orgId, clinicId })
  return res.data
}

/** Record the admin's "Yes, I put it into EyeMed"; resolves with the login's state after it. */
export async function confirmEyemedCodeSetup(orgId: string, clinicId: string): Promise<EyemedLoginState> {
  const fn = httpsCallable<{ orgId: string; clinicId: string }, { state: EyemedLoginState }>(functions, 'confirmEyemedCodeSetup')
  const res = await fn({ orgId, clinicId })
  return res.data.state
}

export interface EyemedSetupPoll {
  state: EyemedLoginState
  /** EyeMed's confirmation code, while it is kept. */
  code: string | null
}

/** The popup's poll: keeps the setup open and hands back the confirmation code. */
export async function getEyemedSetupCode(orgId: string, clinicId: string): Promise<EyemedSetupPoll> {
  const fn = httpsCallable<{ orgId: string; clinicId: string }, EyemedSetupPoll>(functions, 'getEyemedSetupCode')
  const res = await fn({ orgId, clinicId })
  return res.data
}
